import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FileText, Plus, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/cn";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { QueryError } from "@/components/QueryError";
import { AdminRecordActions } from "@/components/AdminRecordActions";

interface FinancialReport {
  id: string;
  reportType: "MONTHLY" | "ANNUAL" | "BUDGET_VS_ACTUAL";
  periodYear: number;
  periodMonth?: number | null;
  fileUrl: string;
  createdAt: string;
}

export function ReportsPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data: reports = [], isLoading, isError } = useQuery({
    queryKey: ["financial-reports"],
    queryFn: async () => (await api.get<FinancialReport[]>("/financial-reports")).data,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/financial-reports/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["financial-reports"] }),
    onError: (e: any) => alert(e?.response?.data?.message ?? "Error"),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText size={24} /> {t("finance.reports")}
        </h1>
        <button onClick={() => setOpen(true)} className="btn-primary">
          <Plus size={16} /> {t("finance.uploadReport")}
        </button>
      </div>

      <div className="card p-0 overflow-x-auto">
        {isError ? (
          <div className="p-6"><QueryError /></div>
        ) : isLoading ? (
          <div className="p-6 text-slate-500">{t("common.loading")}</div>
        ) : reports.length === 0 ? (
          <div className="p-6 text-slate-500">—</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">{t("finance.reportType")}</th>
                <th className="text-left px-4 py-2">{t("finance.year")}</th>
                <th className="text-left px-4 py-2">{t("finance.month")}</th>
                <th className="text-left px-4 py-2">Subido</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {reports.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2"><Badge variant="info">{r.reportType}</Badge></td>
                  <td className="px-4 py-2">{r.periodYear}</td>
                  <td className="px-4 py-2">{r.periodMonth ?? "—"}</td>
                  <td className="px-4 py-2">{formatDate(r.createdAt, i18n.language)}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex items-center gap-3">
                      <a href={r.fileUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1 text-sm">
                        <ExternalLink size={12} /> Abrir
                      </a>
                      <AdminRecordActions
                        compact
                        entityLabel={`${r.reportType} ${r.periodYear}`}
                        onDelete={() => deleteMutation.mutate(r.id)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <UploadModal open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

function UploadModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const now = new Date();
  const [form, setForm] = useState({
    reportType: "MONTHLY" as "MONTHLY" | "ANNUAL" | "BUDGET_VS_ACTUAL",
    periodYear: now.getFullYear(),
    periodMonth: now.getMonth() + 1 as number | null,
    fileUrl: "",
  });

  const mutation = useMutation({
    mutationFn: async () =>
      (await api.post("/financial-reports", {
        ...form,
        periodMonth: form.reportType === "ANNUAL" ? null : form.periodMonth,
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["financial-reports"] });
      onClose();
    },
  });

  return (
    <Modal open={open} onClose={onClose} title={t("finance.uploadReport")}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <div>
          <label className="label">{t("finance.reportType")}</label>
          <select className="input" value={form.reportType} onChange={(e) => setForm({ ...form, reportType: e.target.value as any })}>
            <option value="MONTHLY">{t("finance.monthly")}</option>
            <option value="ANNUAL">{t("finance.annual")}</option>
            <option value="BUDGET_VS_ACTUAL">{t("finance.budgetVsActualType")}</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t("finance.year")}</label>
            <input type="number" className="input" required value={form.periodYear} onChange={(e) => setForm({ ...form, periodYear: parseInt(e.target.value) })} />
          </div>
          {form.reportType !== "ANNUAL" && (
            <div>
              <label className="label">{t("finance.month")}</label>
              <input type="number" min={1} max={12} className="input" required value={form.periodMonth ?? ""} onChange={(e) => setForm({ ...form, periodMonth: parseInt(e.target.value) })} />
            </div>
          )}
        </div>
        <div>
          <label className="label">{t("finance.fileUrl")}</label>
          <input className="input" required placeholder="https://..." value={form.fileUrl} onChange={(e) => setForm({ ...form, fileUrl: e.target.value })} />
          <p className="text-xs text-slate-500 mt-1">Sube el archivo a Cloudinary o Google Drive y pega aquí el enlace.</p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>{t("common.save")}</button>
        </div>
      </form>
    </Modal>
  );
}
